import React from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { BTN_PRIMARY } from "../config/brand";
import { slotDateLabel } from "../lib/slotDates";
import { firstWeekday, slotDaysLabel } from "../lib/weekdays";
import EditableText from "./EditableText";

const DAY_ORDER = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];

const COLS =
  "grid-cols-1 md:grid-cols-[minmax(8rem,1fr)_minmax(9rem,1.3fr)_minmax(8rem,1.2fr)_7rem_8rem_6rem_7rem]";

export function formatSlotTime(time) {
  if (!time) return "—";
  const [h, m] = String(time).split(":");
  if (!h) return time;
  return `${h.replace(/^0/, "") || "0"}h${m && m !== "00" ? m : ""}`;
}

export function sortSlots(slots) {
  return [...slots].sort((a, b) => {
    const center = String(a.center_name || "").localeCompare(String(b.center_name || ""), "fr");
    if (center) return center;
    const offer = String(a.offer_name || a.workshop_name || "").localeCompare(
      String(b.offer_name || b.workshop_name || ""),
      "fr"
    );
    if (offer) return offer;
    const date = String(a.start_date || "").localeCompare(String(b.start_date || ""));
    if (date) return date;
    const day =
      DAY_ORDER.indexOf(firstWeekday(a.days_of_week || a.day_of_week) || String(a.day_of_week || "").toLowerCase()) -
      DAY_ORDER.indexOf(firstWeekday(b.days_of_week || b.day_of_week) || String(b.day_of_week || "").toLowerCase());
    if (day) return day;
    return String(a.start_time || "").localeCompare(String(b.start_time || ""));
  });
}

export default function PlanningSlots({ slots = [], getReserveHref, emptyText, emptyKey }) {
  const rows = sortSlots(slots.filter((s) => s && !s.closed));
  const showDates = rows.some((s) => s.start_date);
  const cols = showDates
    ? COLS
    : "grid-cols-1 md:grid-cols-[minmax(9rem,1.15fr)_minmax(10rem,1.5fr)_7.5rem_8.5rem_6.5rem_7.5rem]";

  if (rows.length === 0) {
    const fallback = emptyText || "Les horaires seront bientôt disponibles.";
    return emptyKey ? (
      <EditableText
        editKey={emptyKey}
        defaultValue={fallback}
        as="p"
        className="text-center text-slate-600 py-10"
      />
    ) : (
      <p className="text-center text-slate-600 py-10">{fallback}</p>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div
        className={`hidden md:grid ${cols} gap-3 px-4 py-2.5 text-[11px] uppercase tracking-[0.08em] text-slate-400 font-medium border-b border-slate-100 bg-slate-50/80`}
      >
        <span>Centre</span>
        <span>Offre</span>
        {showDates ? <span>Date</span> : null}
        <span>Jour</span>
        <span>Horaire</span>
        <span className="tabular-nums">Places</span>
        <span className="text-right">Réserver</span>
      </div>
      <ul className="divide-y divide-slate-100">
        {rows.map((slot) => {
          const full = (slot.available_spots ?? 0) <= 0;
          const offerLabel = slot.offer_name || slot.workshop_name || "—";
          const href = !full && getReserveHref ? getReserveHref(slot) : null;
          const dateLabel = slotDateLabel(slot);
          return (
            <li
              key={`${slot.id}-${slot.start_date || ""}`}
              className={`grid ${cols} gap-1 md:gap-3 px-4 py-3 items-center`}
            >
              <div className="min-w-0">
                <p className="md:hidden text-[11px] uppercase tracking-wider text-slate-400 mb-0.5">
                  Centre
                </p>
                <p className="text-slate-900 font-medium truncate">{slot.center_name || "—"}</p>
              </div>
              <div className="min-w-0">
                <p className="md:hidden text-[11px] uppercase tracking-wider text-slate-400 mb-0.5">
                  Offre
                </p>
                <p className="text-slate-700 text-sm truncate">{offerLabel}</p>
                {slot.formula_name && slot.formula_name !== offerLabel ? (
                  <p className="text-slate-500 text-xs truncate">{slot.formula_name}</p>
                ) : null}
              </div>
              {showDates ? (
              <div>
                <p className="md:hidden text-[11px] uppercase tracking-wider text-slate-400 mb-0.5">
                  Date
                </p>
                <p className="text-slate-700 text-sm capitalize leading-snug">
                  {dateLabel || "—"}
                </p>
                {slot.stage_name ? (
                  <p className="text-slate-500 text-xs truncate">{slot.stage_name}</p>
                ) : null}
              </div>
              ) : null}
              <div>
                <p className="md:hidden text-[11px] uppercase tracking-wider text-slate-400 mb-0.5">
                  Jour
                </p>
                <p className="text-slate-700 capitalize">{slotDaysLabel(slot) || "—"}</p>
              </div>
              <div>
                <p className="md:hidden text-[11px] uppercase tracking-wider text-slate-400 mb-0.5">
                  Horaire
                </p>
                <p className="text-slate-900 font-semibold tabular-nums whitespace-nowrap">
                  {formatSlotTime(slot.start_time)} – {formatSlotTime(slot.end_time)}
                </p>
              </div>
              <div>
                <p className="md:hidden text-[11px] uppercase tracking-wider text-slate-400 mb-0.5">
                  Places
                </p>
                {full ? (
                  <Badge className="bg-red-50 text-red-600 border-red-200">Complet</Badge>
                ) : (
                  <span className="text-slate-600 text-sm tabular-nums whitespace-nowrap">
                    {slot.available_spots ?? "—"}
                    {slot.max_capacity != null ? ` / ${slot.max_capacity}` : ""}
                  </span>
                )}
              </div>
              <div className="md:text-right pt-1 md:pt-0">
                {href ? (
                  <Link to={href}>
                    <Button size="sm" className={BTN_PRIMARY}>
                      Réserver
                    </Button>
                  </Link>
                ) : (
                  <span className="text-sm text-slate-400">—</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
